import { EntityDao } from '@/api/daos/entity-dao';
import { NovelDao } from '@/api/daos/novel-dao';
import { deleteField, Firestore } from 'firebase/firestore';
import type { EntityStore } from '../contracts/entities';
import type {
  Character,
  EntitySummary,
  EntityType,
  Event,
  EventSummary,
  Location,
  Novel,
  NovelStorageLayout,
  Organization,
  Relationship,
  SectionDefinition,
  PrimaryEntityType
} from '@/lib/types';
import {
  getEntity,
  subscribeAllRelationships,
  subscribeChildLocations,
  subscribeEntities,
  subscribeEntity,
  subscribeRelationships
} from './entities/subscriptions';
import {
  loadEntitySettings,
  saveEntitySettings
} from './entities/settings';
import {
  saveEventImplication,
  deleteEventImplication
} from './entities/implications';

export class FirebaseEntityStore implements EntityStore {
  constructor(private firestore: Firestore) { }

  async createEventWithDetails(
    ownerId: string,
    novel: Novel,
    event: Event,
    detailsSummary: string
  ): Promise<void> {
    const { createEventWithDetails } = await import('./events');
    return createEventWithDetails(this.firestore, ownerId, novel, event, detailsSummary);
  }

  subscribeEntity<T extends { id: string }>(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    entityType: EntityType,
    id: string,
    onUpdate: (entity: T | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return subscribeEntity(this.firestore, ownerId, novelId, layout, entityType, id, onUpdate, onError);
  }

  async getEntity<T extends { id: string }>(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    entityType: EntityType,
    id: string
  ): Promise<T | null> {
    return getEntity(this.firestore, ownerId, novelId, layout, entityType, id);
  }

  subscribeEntities<T extends { id: string }>(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    entityType: EntityType,
    onUpdate: (entities: T[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return subscribeEntities(this.firestore, ownerId, novelId, layout, entityType, onUpdate, onError);
  }

  subscribeAllRelationships(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    onUpdate: (relationships: Relationship[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return subscribeAllRelationships(this.firestore, ownerId, novelId, layout, onUpdate, onError);
  }

  subscribeRelationships(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    entityId: string,
    onUpdate: (relationships: Relationship[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return subscribeRelationships(this.firestore, ownerId, novelId, layout, entityId, onUpdate, onError);
  }

  subscribeChildLocations(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    parentLocationId: string,
    onUpdate: (locations: Location[]) => void,
    onError: (error: Error) => void
  ): () => void {
    return subscribeChildLocations(this.firestore, ownerId, novelId, layout, parentLocationId, onUpdate, onError);
  }

  async saveEntity(
    ownerId: string,
    novelId: string,
    entityType: EntityType,
    entity: Character | Location | Organization | Event
  ): Promise<void> {
    const novelDao = new NovelDao(this.firestore);
    const entityDao = new EntityDao(this.firestore);
    const novel = await novelDao.getNovel(ownerId, novelId);
    await entityDao.saveEntity(ownerId, novel, entityType, entity);
  }

  async updateEntity(
    ownerId: string,
    novelId: string,
    entityType: EntityType,
    entityId: string,
    updates: Record<string, unknown>
  ): Promise<void> {
    const novelDao = new NovelDao(this.firestore);
    const entityDao = new EntityDao(this.firestore);
    const novel = await novelDao.getNovel(ownerId, novelId);
    await entityDao.updateEntity(ownerId, novel, entityType, entityId, updates);
  }

  async deleteEntity(
    ownerId: string,
    novelId: string,
    entityType: EntityType,
    entityId: string
  ): Promise<void> {
    const novelDao = new NovelDao(this.firestore);
    const entityDao = new EntityDao(this.firestore);
    const novel = await novelDao.getNovel(ownerId, novelId);
    await entityDao.deleteEntity(ownerId, novel, entityType, entityId);
  }

  async updateEntitySummary(
    ownerId: string,
    novelId: string,
    entityType: EntityType,
    entityId: string,
    summary: EntitySummary | EventSummary | null
  ): Promise<void> {
    const novelDao = new NovelDao(this.firestore);
    const entityDao = new EntityDao(this.firestore);
    const novel = await novelDao.getNovel(ownerId, novelId);
    const updates = summary ? { ...summary } : deleteField();
    await entityDao.updateEntity(ownerId, novel, entityType, entityId, updates);
  }

  async loadEntitySettings(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    entityType: string,
    categoryNameFallback?: string
  ): Promise<SectionDefinition[]> {
    return loadEntitySettings(this.firestore, ownerId, novelId, layout, entityType, categoryNameFallback);
  }

  async saveEntitySettings(
    ownerId: string,
    novelId: string,
    layout: NovelStorageLayout,
    entityType: string,
    settings: SectionDefinition[]
  ): Promise<void> {
    return saveEntitySettings(this.firestore, ownerId, novelId, layout, entityType, settings);
  }

  async saveEventImplication(
    ownerId: string,
    novel: Novel,
    eventId: string,
    eventName: string,
    eventOrder: number,
    entityType: PrimaryEntityType,
    entityId: string,
    entityName: string,
    changes: Array<{ field: string; newValue: unknown }>,
    description: string,
    isEdit: boolean
  ): Promise<void> {
    return saveEventImplication(
      this.firestore,
      ownerId,
      novel,
      eventId,
      eventName,
      eventOrder,
      entityType,
      entityId,
      entityName,
      changes,
      description,
      isEdit
    );
  }

  async deleteEventImplication(
    ownerId: string,
    novel: Novel,
    eventId: string,
    entityType: PrimaryEntityType,
    entityId: string
  ): Promise<void> {
    return deleteEventImplication(this.firestore, ownerId, novel, eventId, entityType, entityId);
  }
}
